import { PROD, resolveDomain, resolveEnv, TITLESOLUTIONS } from '@fluentsms/utils';
import jwtDecode, { JwtPayload } from 'jwt-decode';

export type FluentJwtPayload = JwtPayload & {
  [domainUrl: string]: {
    tenantId: string;
    userId: string;
    displayName: string;
    // Deprecated use securityV2 instead.
    security?: string;
    securityV2: string;
    securityVersion: string;
  };
} & {
  nickname: string;
  name: string;
  picture: string;
  updated_at: string;
  email: string;
  email_verified: string;
};

export class TokenUtility {
  private accessToken: string | null = null;
  private idToken: string | null = null;
  private mfaToken: string | null = null;

  public cacheTokens(accessToken: string, idToken: string): void {
    this.accessToken = accessToken;
    this.idToken = idToken;
  }

  public cacheMfaToken(token: string): void {
    this.mfaToken = token;
  }

  public getAccessToken(): string | null {
    return this.accessToken;
  }

  public getIdToken(): string | null {
    return this.idToken;
  }

  public getMfaToken(): string | null {
    return this.mfaToken;
  }

  public clearStorage(): void {
    this.accessToken = null;
    this.idToken = null;
    this.mfaToken = null;
    sessionStorage.clear();
  }

  /**
   * Used internally for getDecodedToken
   * Can also be used for getCompleteDecodedToken's return object
   */
  public getUserTokenDomain() {
    const env = resolveEnv() === PROD ? '' : `${resolveEnv().toLowerCase()}.`;
    const domain =
      resolveDomain() === TITLESOLUTIONS ? `fluentapi.${resolveDomain()}.com` : `api.${resolveDomain()}.com`;

    return `https://${env}${domain}/users/auth`;
  }

  public getDecodedDetailToken() {
    const decodedToken = this.getCompleteDecodedToken();
    return decodedToken[this.getUserTokenDomain()];
  }

  // This returns the entire token not just inside the environment scope.
  public getCompleteDecodedToken(): FluentJwtPayload {
    return this.extractedToken();
  }

  private extractedToken(): FluentJwtPayload {
    const token = this.getIdToken();
    if (!token) {
      throw new Error('No ID token available. Ensure the user is authenticated before decoding.');
    }
    return jwtDecode<FluentJwtPayload>(token);
  }

  public validateToken(token: string | null) {
    try {
      if (!token) {
        return false;
      }

      const decodedToken: JwtPayload = jwtDecode(token);
      const expireDate = (decodedToken?.exp || 0) * 1000;

      if (Date.now() >= expireDate) {
        return false;
      }

      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Get the minimum expiration time between the idToken and accessToken.
   */
  public getMinimumExpiration(): number {
    const idToken = this.getIdToken();
    const accessToken = this.getAccessToken();

    if (!idToken || !accessToken) return 0;

    try {
      const expirations = [idToken, accessToken]
        .map((token) => jwtDecode<JwtPayload>(token))
        .map(({ exp }) => exp ?? 0);
      return Math.min(...expirations);
    } catch {
      return 0;
    }
  }

  /**
   * Get the maximum issuedAt time between the idToken and accessToken.
   */
  public getMaximumIssuedAt(): number {
    const idToken = this.getIdToken();
    const accessToken = this.getAccessToken();

    if (!idToken || !accessToken) return 0;

    try {
      const issuedAts = [idToken, accessToken].map((token) => jwtDecode<JwtPayload>(token)).map(({ iat }) => iat ?? 0);
      return Math.max(...issuedAts);
    } catch {
      return 0;
    }
  }
}

export const tokenUtility = new TokenUtility();

export default tokenUtility;
